import { Component, ViewChild } from "@angular/core";
import { SlickDialogComponent } from "@slick-components";
import { ACHPaymentApprovedModel, ACHProcessPaymentModel, CreditCardPaymentApprovedModel, CreditCardProcessPaymentModel, IACHPaymentApprovedModel, IACHPaymentTypeModel, IACHProcessPaymentModel, IACHTransactionLogModel, ICreditCardPaymentApprovedModel, ICreditCardPaymentTypeModel, ICreditCardProcessPaymentModel, ICreditCardTransactionLogModel, IInvoiceGridModel, IInvoiceListModel, IInvoiceModel, IPaymentModel } from "@models";
import { BatchPaymentModel, IBatchPaymentModel } from "@models/invoices/batch-payment-model";
import { PaymentProcessorService, GlobalsService, UtilsService, LookupService, } from "@services";
import { ICustomerTypeModel } from "../../models/customers/customer-type.model";

@Component({
    selector: "batch-payment-dialog",
    templateUrl: "./batch-payment-dialog.component.html",
    styleUrl: "./batch-payment-dialog.component.scss",
    providers: [PaymentProcessorService]
})
export class BatchPaymentDialogComponent {
    @ViewChild("dialogRef", { static: true }) dialogRef: SlickDialogComponent;
    @ViewChild("authDialogRef", { static: true }) authDialogRef: SlickDialogComponent;


    ctrlKeyDown: boolean = false;
    paymentModels: IBatchPaymentModel[] = [];
    creditCardPaymentTypes: ICreditCardPaymentTypeModel[];
    achPaymentTypes: IACHPaymentTypeModel[];
    selectedPaymentTypeModel: ICreditCardPaymentTypeModel;
    selectedACHPaymentTypeModel: IACHPaymentTypeModel;
    creditCardProcessPaymentModel: ICreditCardProcessPaymentModel;
    achProcessPaymentModel: IACHProcessPaymentModel;
    creditCardTransactionLogModel: ICreditCardTransactionLogModel;
    achTransactionLogModel: IACHTransactionLogModel;
    prePaymentSpinner: string;
    paymentSpinner: string;
    isQuickbooksEnabled = GlobalsService.isQuickbooksEnabled;
    isCanada = (GlobalsService.company.companyId === 3);
    syncToQuickbooks: boolean;
    customerInvoices: IInvoiceListModel[];
    approvedPaymentUuid: string;
    isProcessing: boolean;
    processedPayments: IBatchPaymentModel[] = [];
    showCreditCardProcessingFee: boolean;
    showACH: boolean = GlobalsService.company.useACH;
    selectedPaymentType: string = 'creditCard';
    gridModels: IInvoiceGridModel[];

    customerTypes: ICustomerTypeModel[];

    private onResolve: any;

    constructor(private paymentProcessorService: PaymentProcessorService, private lookupService: LookupService) {
        this.customerTypes = this.lookupService.getCustomerTypes();
    }



    async showDialog(gridModels: IInvoiceGridModel[]): Promise<IBatchPaymentModel[]> {
        return new Promise<IBatchPaymentModel[]>(async (resolve) => {
            this.paymentModels = [];
            this.processedPayments = [];
            this.gridModels = [...gridModels];
            this.showCreditCardProcessingFee = GlobalsService.company.useCreditCardProcessingFees 

            this.populateBatchPaymentModels(this.selectedPaymentType === 'creditCard');

            this.onResolve = resolve;
            await this.dialogRef.showDialog();


        });
    }

    populateBatchPaymentModels(includeCCFee: boolean) {
        this.paymentModels = [];
        this.gridModels.forEach(x => {
            let customerPayment = this.paymentModels.find(payment => payment.customerId === x.customerId);

            if (!customerPayment) {
                customerPayment = new BatchPaymentModel();

                this.paymentModels.push(customerPayment);
            }
            customerPayment.invoiceGridModels.push(x);
            customerPayment.invoiceIds.push(x.invoiceId);
            customerPayment.customerId = x.customerId;
            customerPayment.customerName = x.displayName;
            customerPayment.customerTypeId = x.customerTypeId;
            customerPayment.total += x.outstandingBalance;
        });

        // after we get the totals we need to populate the cc fee

        if (this.showCreditCardProcessingFee && includeCCFee) {
            this.paymentModels.forEach(x => {
                let fee = this.customerTypes.find(ct => x.customerTypeId === ct.customerTypeId)?.creditCardFeePercent ?? 0;
                if (!fee || fee === 0) {
                    x.CreditCardProcessingFeeTotal = 0;
                }
                else {

                    x.CreditCardProcessingFeeTotal = UtilsService.round(x.total * (fee / 100))
                    x.total += UtilsService.round(x.total * fee / 100)
                }
            })
        }


    }

    setPaymentType(type: string) {
        this.selectedPaymentType = type;
        this.populateBatchPaymentModels(type === 'creditCard' ? true : false);
    }

    toggleShowInvoices(paymentModel: IBatchPaymentModel) {
        if (paymentModel.showInvoices)
            paymentModel.showInvoices = false;
        else
            paymentModel.showInvoices = true;
    }


    async processPayment() {
        this.isProcessing = true;
        for (let paymentModel of this.paymentModels) {
            if (!this.isProcessing) {
                break;
            }
            if (paymentModel.total === 0) {
                paymentModel.status = "No payment ran for $0";
            }
            else if (!this.processedPayments.find(x => x === paymentModel)) {
                paymentModel.isProcessing = true;
                if (this.selectedPaymentType === 'creditCard') {
                    const creditCardProcessPaymentModel = new CreditCardProcessPaymentModel();
                    creditCardProcessPaymentModel.invoiceIds = paymentModel.invoiceIds;
                    creditCardProcessPaymentModel.amount = paymentModel.total;
                    creditCardProcessPaymentModel.includeCCFee = this.showCreditCardProcessingFee && (paymentModel.CreditCardProcessingFeeTotal ?? 0) > 0;

                    this.creditCardPaymentTypes = null;
                    this.creditCardTransactionLogModel = null;
                    this.selectedPaymentTypeModel = null;
                    this.syncToQuickbooks = true;


                    if (creditCardProcessPaymentModel) {
                        this.creditCardProcessPaymentModel = creditCardProcessPaymentModel;
                        this.creditCardProcessPaymentModel.customerId = paymentModel.customerId;

                    }

                    this.creditCardPaymentTypes = await this.paymentProcessorService.getCreditCardPaymentTypeForCustomer(paymentModel.customerId);

                    if (this.creditCardPaymentTypes.length > 0) {
                        this.selectedPaymentTypeModel = this.creditCardPaymentTypes.find(x => x.isDefault === true);
                        if (!this.selectedPaymentTypeModel)
                            this.selectedPaymentTypeModel = this.creditCardPaymentTypes[0];
                    }
                    else {
                        paymentModel.status = 'No card on file.';
                    }
                    if (this.selectedPaymentTypeModel) {
                        this.creditCardProcessPaymentModel.creditCardPaymentTypeId = this.selectedPaymentTypeModel.creditCardPaymentTypeId;
                        this.creditCardProcessPaymentModel.syncToQuickbooks = this.syncToQuickbooks;
                        const creditCardProcessPaymentResult = await this.paymentProcessorService.processPayment(this.creditCardProcessPaymentModel);

                        this.creditCardTransactionLogModel = creditCardProcessPaymentResult.creditCardTransactionLogModel;
                        paymentModel.status = this.creditCardTransactionLogModel.errorMessage;
                        if (this.creditCardTransactionLogModel.result === 'Approved') {
                            const creditCardPaymentApprovedModel: ICreditCardPaymentApprovedModel = new CreditCardPaymentApprovedModel();
                            creditCardPaymentApprovedModel.creditCardTransactionLogModel = creditCardProcessPaymentResult.creditCardTransactionLogModel;
                            creditCardPaymentApprovedModel.paymentModel = creditCardProcessPaymentResult.paymentModel;
                            creditCardPaymentApprovedModel.appliedPayment = creditCardProcessPaymentResult.appliedPaymentModel;
                            paymentModel.status = this.creditCardTransactionLogModel.status;
                            paymentModel.canClickStatus = true;
                            paymentModel.transactionLog = this.creditCardTransactionLogModel;
                        }

                    }
                }
                else {
                    const achProcessPaymentModel = new ACHProcessPaymentModel();
                    achProcessPaymentModel.invoiceIds = paymentModel.invoiceIds;
                    achProcessPaymentModel.amount = paymentModel.total;

                    this.creditCardPaymentTypes = null;
                    this.creditCardTransactionLogModel = null;
                    this.selectedPaymentTypeModel = null;
                    this.syncToQuickbooks = true;


                    if (achProcessPaymentModel) {
                        this.achProcessPaymentModel = achProcessPaymentModel;
                        this.achProcessPaymentModel.customerId = paymentModel.customerId;

                    }

                    this.achPaymentTypes = await this.paymentProcessorService.getACHPaymentTypeForCustomer(paymentModel.customerId);

                    if (this.achPaymentTypes.length > 0) {
                        this.selectedACHPaymentTypeModel = this.achPaymentTypes.find(x => x.isDefault === true);
                        if (!this.selectedACHPaymentTypeModel)
                            this.selectedACHPaymentTypeModel = this.achPaymentTypes[0];
                    }
                    else {
                        paymentModel.status = 'No ACH on file.';
                    }
                    if (this.selectedACHPaymentTypeModel) {
                        this.achProcessPaymentModel.achPaymentTypeId = this.selectedACHPaymentTypeModel.achPaymentTypeId;
                        this.achProcessPaymentModel.syncToQuickbooks = this.syncToQuickbooks;
                        const achProcessPaymentResult = await this.paymentProcessorService.processACHPayment(this.achProcessPaymentModel);

                        this.achTransactionLogModel = achProcessPaymentResult.achTransactionLogModel;
                        paymentModel.status = this.achTransactionLogModel.errorMessage;
                        if (this.achTransactionLogModel.result === 'Approved') {
                            const achPaymentApprovedModel: IACHPaymentApprovedModel = new ACHPaymentApprovedModel();
                            achPaymentApprovedModel.achTransactionLogModel = achProcessPaymentResult.achTransactionLogModel;
                            achPaymentApprovedModel.paymentModel = achProcessPaymentResult.paymentModel;
                            achPaymentApprovedModel.appliedPayment = achProcessPaymentResult.appliedPaymentModel;
                            paymentModel.status = this.achTransactionLogModel.status;
                            paymentModel.canClickStatus = true;
                            paymentModel.achTransactionLog = this.achTransactionLogModel;
                        }

                    }
                }
                paymentModel.isProcessing = false;
                this.processedPayments.push(paymentModel);
            }
        }
        this.isProcessing = false;
    }

    stopProcessingPayments() {
        this.isProcessing = false;
    }

    continue() {
        this.onResolve(this.processedPayments);
        this.dialogRef.hideDialog();
    }

    cancel() {
        this.onResolve(this.processedPayments);
        this.dialogRef.hideDialog();
    }

    openStatus(paymentModel: IBatchPaymentModel) {
        this.creditCardTransactionLogModel = paymentModel.transactionLog;
        this.achTransactionLogModel = paymentModel.achTransactionLog;
        this.authDialogRef.showDialog();
    }

    onApprovedOk() {
        this.authDialogRef.hideDialog();
    }
}
